import { type ReactNode, Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { LoginPage } from '../auth/LoginPage';
import { MfaPage } from '../auth/MfaPage';
import { SignupPage } from '../auth/SignupPage';
import { AuthProvider } from '../shared/auth/AuthContext';
import { RequireAuth } from '../shared/auth/RequireAuth';
import { SplashLoader } from '../shared/components/SplashLoader';
import { I18nProvider } from '../shared/i18n/I18nContext';
import { ThemeProvider } from '../shared/theme/ThemeProvider';

import { ErrorBoundary } from './ErrorBoundary';
import { Home } from './Home';
import { NotFoundPage } from './NotFoundPage';

// ============================================================
// M3.3 — Lazy routes para reduzir bundle inicial
// ============================================================
// Públicas (eager): Home, Login, Signup, MfaPage, NotFound.
// Lazy:
//   - AppShellWithQuery (carrega @tanstack/react-query + AppShell)
//   - CalmaPage (mesmo no caminho público /calma — Modo Calma é fora
//     do critical first-paint).
//   - Todas as telas /app/* — autista N1/N2/N3, cuidador, CAA, crise.
//
// Os imports estáticos no entry-graph dependem APENAS de:
//   - firebase/app, firebase/auth (camada 1)
//   - react, react-dom, react-router-dom
//   - i18n catalog (~ KB)
// Firestore/Functions/Storage/Messaging permanecem fora do bundle
// público até a primeira navegação para /app/* ou após login.
// ============================================================

const AppShellWithQuery = lazy(() =>
  import('./AppShellWithQuery').then((m) => ({ default: m.AppShellWithQuery })),
);
const AppShellHome = lazy(() =>
  import('./AppShellHome').then((m) => ({ default: m.AppShellHome })),
);
const CalmaPage = lazy(() => import('../calma/CalmaPage').then((m) => ({ default: m.CalmaPage })));

// Rotas autenticadas — todos os módulos pesados (Firestore, Functions,
// Storage) entram no bundle via estes chunks lazy.
const AutistaRouter = lazy(() =>
  import('../autista/AutistaRouter').then((m) => ({ default: m.AutistaRouter })),
);
const N1Home = lazy(() => import('../autista/n1/N1Home').then((m) => ({ default: m.N1Home })));
const MoodEntryN1 = lazy(() =>
  import('../autista/n1/MoodEntryN1').then((m) => ({ default: m.MoodEntryN1 })),
);
const SensoryEventN1 = lazy(() =>
  import('../autista/n1/SensoryEventN1').then((m) => ({ default: m.SensoryEventN1 })),
);
const AssistenteN1 = lazy(() =>
  import('../autista/n1/AssistenteN1').then((m) => ({ default: m.AssistenteN1 })),
);
const HistoricoAutista = lazy(() =>
  import('../autista/HistoricoAutista').then((m) => ({ default: m.HistoricoAutista })),
);
const DistrairHome = lazy(() =>
  import('../distrair/DistrairHome').then((m) => ({ default: m.DistrairHome })),
);
const N2Home = lazy(() => import('../autista/n2/N2Home').then((m) => ({ default: m.N2Home })));
const MoodEntryN2 = lazy(() =>
  import('../autista/n2/MoodEntryN2').then((m) => ({ default: m.MoodEntryN2 })),
);
const N3Home = lazy(() => import('../autista/n3/N3Home').then((m) => ({ default: m.N3Home })));
const MoodEntryN3 = lazy(() =>
  import('../autista/n3/MoodEntryN3').then((m) => ({ default: m.MoodEntryN3 })),
);

const CuidadorHome = lazy(() =>
  import('../cuidador/CuidadorHome').then((m) => ({ default: m.CuidadorHome })),
);
const RotinaDiaria = lazy(() =>
  import('../cuidador/RotinaDiaria').then((m) => ({ default: m.RotinaDiaria })),
);
const RegistroHumorCuidador = lazy(() =>
  import('../cuidador/RegistroHumorCuidador').then((m) => ({
    default: m.RegistroHumorCuidador,
  })),
);
const RegistroCriseCuidador = lazy(() =>
  import('../cuidador/RegistroCriseCuidador').then((m) => ({
    default: m.RegistroCriseCuidador,
  })),
);
const RegistroABCCuidador = lazy(() =>
  import('../cuidador/RegistroABCCuidador').then((m) => ({ default: m.RegistroABCCuidador })),
);
const RegistroSonoCuidador = lazy(() =>
  import('../cuidador/RegistroSonoCuidador').then((m) => ({
    default: m.RegistroSonoCuidador,
  })),
);
const MarcaDoseCuidador = lazy(() =>
  import('../cuidador/MarcaDoseCuidador').then((m) => ({ default: m.MarcaDoseCuidador })),
);

const CaaPage = lazy(() => import('../caa/CaaPage').then((m) => ({ default: m.CaaPage })));

const PainelLayout = lazy(() =>
  import('../painel/PainelLayout').then((m) => ({ default: m.PainelLayout })),
);
const PatientList = lazy(() =>
  import('../painel/PatientList').then((m) => ({ default: m.PatientList })),
);
const PatientChart = lazy(() =>
  import('../painel/PatientChart').then((m) => ({ default: m.PatientChart })),
);

const AdminDashboard = lazy(() =>
  import('../admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
);
const DemoTour = lazy(() => import('../admin/DemoTour').then((m) => ({ default: m.DemoTour })));
const WearableDemoPage = lazy(() =>
  import('../admin/WearableDemoPage').then((m) => ({ default: m.WearableDemoPage })),
);

const CrisePage = lazy(() => import('../crise/CrisePage').then((m) => ({ default: m.CrisePage })));
const PassosGuiados = lazy(() =>
  import('../crise/PassosGuiados').then((m) => ({ default: m.PassosGuiados })),
);
const SamuButton = lazy(() =>
  import('../crise/SamuButton').then((m) => ({ default: m.SamuButton })),
);
const RevogarGeolocalizacao = lazy(() =>
  import('../crise/RevogarGeolocalizacao').then((m) => ({
    default: m.RevogarGeolocalizacao,
  })),
);

export interface AppProps {
  router?: 'browser' | 'none';
}

/**
 * Fallback acessível para Suspense. SplashLoader com identidade Lume.
 * Substitui o Spinner genérico para apresentações multidisciplinares.
 */
function RouteFallback(): ReactNode {
  return <SplashLoader fullscreen />;
}

/**
 * Root da PWA. Em testes pode-se passar `router="none"` quando o teste já provê
 * um router próprio (MemoryRouter/Routes) — nesse caso o BrowserRouter é omitido.
 */
export function App({ router = 'browser' }: AppProps = {}) {
  const tree = (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/mfa" element={<MfaPage />} />
        {/* Modo Calma: rota pública (modo visitante) — alvo do shortcut do PWA. */}
        <Route path="/calma" element={<CalmaPage visitor />} />
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppShellWithQuery />
            </RequireAuth>
          }
        >
          <Route index element={<AppShellHome />} />

          <Route path="autista" element={<AutistaRouter />} />
          <Route path="autista/historico" element={<HistoricoAutista />} />
          <Route path="autista/distrair" element={<DistrairHome />} />
          <Route path="autista/n1" element={<N1Home />} />
          <Route path="autista/n1/humor" element={<MoodEntryN1 />} />
          <Route path="autista/n1/sensorial" element={<SensoryEventN1 />} />
          <Route path="autista/n1/assistente" element={<AssistenteN1 />} />
          <Route path="autista/n2" element={<N2Home />} />
          <Route path="autista/n2/humor" element={<MoodEntryN2 />} />
          <Route path="autista/n3" element={<N3Home />} />
          <Route path="autista/n3/humor" element={<MoodEntryN3 />} />

          <Route
            path="cuidador"
            element={
              <RequireAuth roles={['caregiver']}>
                <CuidadorHome />
              </RequireAuth>
            }
          />
          <Route path="cuidador/:pid/rotina" element={<RotinaDiaria />} />
          <Route path="cuidador/:pid/humor" element={<RegistroHumorCuidador />} />
          <Route path="cuidador/:pid/crise" element={<RegistroCriseCuidador />} />
          <Route path="cuidador/:pid/abc" element={<RegistroABCCuidador />} />
          <Route path="cuidador/:pid/sono" element={<RegistroSonoCuidador />} />
          <Route path="cuidador/:pid/dose" element={<MarcaDoseCuidador />} />
          <Route path="cuidador/:pid/distrair" element={<DistrairHome />} />

          <Route
            path="painel"
            element={
              <RequireAuth roles={['physician', 'multidisciplinary', 'admin']}>
                <PainelLayout />
              </RequireAuth>
            }
          >
            <Route index element={<PatientList />} />
            <Route path=":pid" element={<PatientChart />} />
          </Route>

          <Route
            path="admin"
            element={
              <RequireAuth roles={['admin']}>
                <AdminDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="admin/tour"
            element={
              <RequireAuth roles={['admin']}>
                <DemoTour />
              </RequireAuth>
            }
          />
          <Route
            path="admin/demo/wearable"
            element={
              <RequireAuth roles={['admin']}>
                <WearableDemoPage />
              </RequireAuth>
            }
          />

          <Route path="caa" element={<CaaPage />} />
          <Route path="calma" element={<CalmaPage />} />
          <Route path="crise" element={<CrisePage />} />
          <Route path="crise/passos" element={<PassosGuiados />} />
          <Route path="crise/samu" element={<SamuButton standalone />} />
          <Route path="crise/revogar-geo" element={<RevogarGeolocalizacao />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );

  const wrapped = (
    <ErrorBoundary>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>{tree}</AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );

  if (router === 'none') return wrapped;
  return <BrowserRouter>{wrapped}</BrowserRouter>;
}
